import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell,
} from 'recharts';
import { depenseService } from '../../services/api';
import { ChevronLeft, ChevronRight, TrendingUp, Clock, AlertTriangle, Target, Wallet } from 'lucide-react';

const MOIS_FR = [
  'Janvier','Février','Mars','Avril','Mai','Juin',
  'Juillet','Août','Septembre','Octobre','Novembre','Décembre',
];

const PALETTE = ['#0d9488','#0ea5e9','#6366f1','#f59e0b','#ef4444','#8b5cf6','#ec4899','#14b8a6'];

const fmt = (n: number) =>
  new Intl.NumberFormat('fr-GN', { style: 'decimal', maximumFractionDigits: 0 }).format(n);

const fmtShort = (n: number) => {
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}Md`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return String(n);
};

const DepenseDashboard: React.FC = () => {
  const now = new Date();
  const [mois, setMois] = useState(now.getMonth() + 1);
  const [annee, setAnnee] = useState(now.getFullYear());

  const { data: statsData, isLoading } = useQuery({
    queryKey: ['depenses-statistiques', mois, annee],
    queryFn: () => depenseService.getStatistiques({ mois, annee }),
  });

  const stats = statsData?.data;

  const prevMois = () => {
    if (mois === 1) { setMois(12); setAnnee(a => a - 1); }
    else setMois(m => m - 1);
  };
  const nextMois = () => {
    const now2 = new Date();
    if (annee === now2.getFullYear() && mois === now2.getMonth() + 1) return;
    if (mois === 12) { setMois(1); setAnnee(a => a + 1); }
    else setMois(m => m + 1);
  };
  const isCurrentMonth = annee === now.getFullYear() && mois === now.getMonth() + 1;

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-white border border-gray-100 rounded-xl shadow-lg px-4 py-3 text-sm">
        <p className="font-semibold text-gray-700 mb-1">{label}</p>
        <p className="text-teal-600 font-bold">{fmt(payload[0].value)} GNF</p>
      </div>
    );
  };

  if (isLoading) return (
    <div className="flex items-center justify-center h-64">
      <div className="flex flex-col items-center gap-3">
        <div className="w-10 h-10 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
        <span className="text-sm text-gray-400">Chargement…</span>
      </div>
    </div>
  );

  if (!stats) return (
    <div className="text-center py-16 text-gray-400">Aucune donnée disponible</div>
  );

  const budgetPct = Math.min(stats.pourcentage_budget || 0, 100);
  const budgetColor = budgetPct > 80 ? '#ef4444' : budgetPct > 60 ? '#f59e0b' : '#0d9488';

  return (
    <div className="space-y-6">

      {/* ── Sélecteur de période ── */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-2xl shadow-sm px-2 py-1">
          <button
            onClick={prevMois}
            className="w-8 h-8 flex items-center justify-center rounded-xl hover:bg-gray-100 text-gray-500 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="px-4 py-1 min-w-[160px] text-center font-semibold text-gray-800 text-sm">
            {MOIS_FR[mois - 1]} {annee}
          </span>
          <button
            onClick={nextMois}
            disabled={isCurrentMonth}
            className="w-8 h-8 flex items-center justify-center rounded-xl hover:bg-gray-100 text-gray-500 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        {!isCurrentMonth && (
          <button
            onClick={() => { setMois(now.getMonth() + 1); setAnnee(now.getFullYear()); }}
            className="text-xs text-teal-600 hover:text-teal-700 font-medium border border-teal-200 rounded-xl px-3 py-1.5 hover:bg-teal-50 transition-colors"
          >
            Mois courant
          </button>
        )}
      </div>

      {/* ── KPI Cards ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">

        {/* Dépenses du mois */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-center gap-4">
          <div className="w-12 h-12 bg-teal-50 rounded-xl flex items-center justify-center flex-shrink-0">
            <Wallet className="w-5 h-5 text-teal-600" />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">Dépenses du mois</p>
            <p className="text-xl font-bold text-gray-900 mt-0.5 truncate">{fmt(stats.total_mois)} <span className="text-sm font-normal text-gray-400">GNF</span></p>
            <p className="text-xs text-gray-400 mt-0.5">{stats.nombre_depenses_mois} dépenses</p>
          </div>
        </div>

        {/* En attente */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-center gap-4">
          <div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center flex-shrink-0">
            <Clock className="w-5 h-5 text-amber-500" />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">En attente</p>
            <p className="text-xl font-bold text-amber-600 mt-0.5 truncate">{fmt(stats.montant_en_attente)} <span className="text-sm font-normal text-gray-400">GNF</span></p>
            <p className="text-xs text-gray-400 mt-0.5">{stats.nombre_en_attente} dépenses</p>
          </div>
        </div>

        {/* En retard */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-center gap-4">
          <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-5 h-5 text-red-500" />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">En retard</p>
            <p className="text-xl font-bold text-red-600 mt-0.5">{stats.nombre_en_retard}</p>
            <p className="text-xs text-gray-400 mt-0.5">à régler urgemment</p>
          </div>
        </div>

        {/* Budget */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-center gap-4">
          <div className="w-12 h-12 bg-indigo-50 rounded-xl flex items-center justify-center flex-shrink-0">
            <Target className="w-5 h-5 text-indigo-500" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">Budget mensuel</p>
            {stats.budget_mensuel ? (
              <>
                <p className="text-xl font-bold mt-0.5" style={{ color: budgetColor }}>
                  {budgetPct.toFixed(0)}%
                </p>
                <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{ width: `${budgetPct}%`, backgroundColor: budgetColor }}
                  />
                </div>
                <p className="text-xs text-gray-400 mt-1">Reste : {fmt(stats.solde_budget || 0)} GNF</p>
              </>
            ) : (
              <p className="text-sm text-gray-400 mt-1">Non défini</p>
            )}
          </div>
        </div>
      </div>

      {/* ── Graphiques ── */}
      <div className="grid grid-cols-1 xl:grid-cols-5 gap-5">

        {/* Évolution — 3 colonnes */}
        <div className="xl:col-span-3 bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-5">Évolution sur 12 mois</h3>
          {stats.evolution_mensuelle?.length > 0 ? (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.evolution_mensuelle} barSize={20}>
                  <defs>
                    <linearGradient id="barGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#0d9488" />
                      <stop offset="100%" stopColor="#0ea5e9" stopOpacity={0.7} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                  <XAxis dataKey="mois_court" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                  <YAxis tickFormatter={fmtShort} tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} width={50} />
                  <Tooltip content={<CustomTooltip />} cursor={{ fill: '#f0fdfa', radius: 6 }} />
                  <Bar dataKey="total" fill="url(#barGrad)" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-72 flex items-center justify-center text-gray-300 text-sm">Aucune donnée</div>
          )}
        </div>

        {/* Répartition par catégorie — 2 colonnes */}
        <div className="xl:col-span-2 bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-5">Par catégorie — {MOIS_FR[mois - 1]}</h3>
          {stats.depenses_par_categorie?.length > 0 ? (
            <>
              <div className="h-44 mb-4">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={stats.depenses_par_categorie.map((cat, i) => ({
                        name: cat.categorie__nom,
                        value: cat.total,
                      }))}
                      cx="50%" cy="50%"
                      innerRadius={48} outerRadius={72}
                      paddingAngle={3}
                      dataKey="value"
                    >
                      {stats.depenses_par_categorie.map((_, i) => (
                        <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v: number) => [`${fmt(v)} GNF`, '']} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-2">
                {stats.depenses_par_categorie.slice(0, 5).map((cat, i) => {
                  const total = stats.depenses_par_categorie.reduce((s, c) => s + c.total, 0);
                  const pct = total > 0 ? (cat.total / total * 100).toFixed(0) : '0';
                  return (
                    <div key={i} className="flex items-center gap-2 text-xs">
                      <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: PALETTE[i % PALETTE.length] }} />
                      <span className="flex-1 text-gray-600 truncate">{cat.categorie__nom}</span>
                      <span className="font-semibold text-gray-800">{pct}%</span>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="h-72 flex items-center justify-center text-gray-300 text-sm">Aucune dépense</div>
          )}
        </div>
      </div>

      {/* ── Bas de page : Total annuel + Top fournisseurs ── */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">

        {/* Total annuel */}
        <div className="xl:col-span-1 rounded-2xl p-6 flex flex-col justify-between"
          style={{ background: 'linear-gradient(135deg, #0d9488 0%, #0369a1 100%)' }}>
          <div>
            <p className="text-teal-100 text-xs font-medium uppercase tracking-wider mb-2">Total {annee}</p>
            <p className="text-white text-3xl font-bold">{fmtShort(stats.total_annee)}</p>
            <p className="text-teal-200 text-sm mt-1">GNF</p>
          </div>
          <div className="mt-6 flex items-center gap-2 text-teal-100 text-xs">
            <TrendingUp className="w-4 h-4" />
            <span>Dépenses payées cette année</span>
          </div>
        </div>

        {/* Top fournisseurs */}
        <div className="xl:col-span-2 bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Top fournisseurs — {annee}</h3>
          {stats.top_fournisseurs?.length > 0 ? (
            <div className="space-y-3">
              {stats.top_fournisseurs.map((f, i) => {
                const max = stats.top_fournisseurs[0].total;
                const pct = max > 0 ? (f.total / max * 100) : 0;
                return (
                  <div key={i} className="flex items-center gap-3">
                    <span className="w-5 text-xs font-bold text-gray-300">#{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-baseline mb-1">
                        <span className="text-sm font-medium text-gray-700 truncate">{f.fournisseur__nom}</span>
                        <span className="text-xs text-gray-400 ml-2 flex-shrink-0">{f.count} pmt</span>
                      </div>
                      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full"
                          style={{ width: `${pct}%`, background: PALETTE[i % PALETTE.length] }}
                        />
                      </div>
                      <span className="text-xs text-gray-500 mt-0.5 block">{fmt(f.total)} GNF</span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-300 text-sm">Aucun paiement fournisseur</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DepenseDashboard;
